import React, { useEffect, useState } from "react";
import { Button, Form } from "antd";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { login } from "../../actions/auth";
import apiClient from "../../actions/api";
import openNotification from "../../components/Notifications/openNotification";
import LogoSrc from "../../assets/image/logo.svg";
import VerificationCodeInput from "./VerificationCodeInput";
import "../../style/auth/auth.scss";

const EmailVerification = ({ title }) => {
  document.title = title;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [verificationToken, setVerificationToken] = useState(
    location.state?.verificationToken || "",
  );
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const email = location.state?.email || "your email address";

  useEffect(() => {
    if (isAuthenticated) navigate("/dashboard");
    else if (!verificationToken) navigate("/login", { replace: true });
  }, [isAuthenticated, navigate, verificationToken]);

  const showErrors = (err) => {
    const errors = err?.response?.data?.errors;
    if (errors?.length) {
      errors.forEach(({ msg }) =>
        openNotification("error", "Verification Error", msg),
      );
    } else {
      openNotification(
        "error",
        "Verification Error",
        "Unable to verify your email.",
      );
    }
  };

  const onFinish = async ({ code }) => {
    setLoading(true);
    try {
      const res = await apiClient.post("/users/verify-email", {
        code,
        verificationToken,
      });
      dispatch(login(res.data.token));
      openNotification(
        "success",
        "Email verified",
        "You've signed in successfully.",
      );
    } catch (err) {
      showErrors(err);
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setResending(true);
    try {
      const res = await apiClient.post("/users/resend-verification", {
        verificationToken,
      });
      setVerificationToken(res.data.verificationToken);
      openNotification(
        "success",
        "Code sent",
        "A new 8-digit code was sent to your email.",
      );
    } catch (err) {
      showErrors(err);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="product-auth-page">
      <div className="product-auth-shell">
        <aside className="product-auth-brand">
          <div className="product-auth-logo">
            <img src={LogoSrc} alt="" />
            <span>
              Resume <strong>Builder</strong>
            </span>
          </div>
          <div className="product-auth-brand-copy">
            <span className="product-auth-eyebrow">Secure sign in</span>
            <h1>One quick security check.</h1>
            <p>
              Confirm that you own this email address before entering your
              workspace.
            </p>
          </div>
          <div className="product-auth-trust">
            <SafetyCertificateOutlined />
            <span>
              <strong>Protected access</strong>
              <small>Your code expires after 10 minutes.</small>
            </span>
          </div>
        </aside>
        <section className="product-auth-card product-auth-card-verification">
          <div className="product-auth-heading">
            <span>Email verification</span>
            <h2>Enter your 8-digit code</h2>
            <p>
              We sent the code to <strong>{email}</strong>.
            </p>
          </div>
          <Form layout="vertical" onFinish={onFinish}>
            <Form.Item
              label="Verification code"
              name="code"
              rules={[
                {
                  required: true,
                  message: "Please enter the verification code",
                },
                {
                  pattern: /^\d{8}$/,
                  message: "The code must contain exactly 8 digits",
                },
              ]}
            >
              <VerificationCodeInput disabled={loading} />
            </Form.Item>
            <Form.Item className="product-auth-submit">
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={loading}
              >
                Verify and Sign In
              </Button>
            </Form.Item>
          </Form>
          <div className="product-auth-switch">
            Didn't receive it?{" "}
            <Button type="link" onClick={resendCode} loading={resending}>
              Send a new code
            </Button>
          </div>
          <div className="product-auth-switch">
            <Button type="link" onClick={() => navigate("/login")}>
              Back to sign in
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default EmailVerification;
